'use client'

import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../../_components/_ui/background-beams'
import Link from 'next/link'
import { FaImage, FaLink, FaMarkdown, FaColumns, FaCheckSquare, FaCode, FaReceipt, FaShareAlt, FaCubes, FaAdversal, FaMusic, FaShoppingCart, FaMapMarkerAlt, FaCog } from 'react-icons/fa'

const blockTypes = [
    {
        name: 'Text/Markdown Block',
        icon: FaMarkdown,
        description: 'Rich text content with full markdown support including headers, lists, links, and formatted text.',
        features: ['GitHub-flavored markdown', 'Syntax highlighting', 'Table support', 'Live preview'],
        apiExample: `{
  "type": "markdown",
  "content": {
    "text": "# My heading\\n\\nThis is **bold** text with a [link](https://example.com)."
  }
}`,
        color: 'border-purple-500/20 bg-purple-500/5'
    },
    {
        name: 'Image Block',
        icon: FaImage,
        description: 'Single image display with optional captions, alt text, and click-through links.',
        features: ['WebP/AVIF optimization', 'Responsive sizing', 'Lazy loading', 'Alt text support'],
        apiExample: `{
  "type": "image",
  "content": {
    "url": "https://example.com/image.jpg",
    "alt": "Description",
    "caption": "Image caption"
  }
}`,
        color: 'border-blue-500/20 bg-blue-500/5'
    },
    {
        name: 'Image Gallery Block',
        icon: FaImage,
        description: 'Multiple images in a responsive grid layout with lightbox functionality.',
        features: ['Grid layouts', 'Lightbox viewing', 'Bulk upload', 'Image reordering'],
        apiExample: `{
  "type": "image_gallery",
  "content": {
    "images": [
      {"url": "image1.jpg", "caption": "Photo 1"},
      {"url": "image2.jpg", "caption": "Photo 2"}
    ]
  }
}`,
        color: 'border-cyan-500/20 bg-cyan-500/5'
    },
    {
        name: 'Link Block',
        icon: FaLink,
        description: 'Rich link previews with automatic metadata fetching and custom styling.',
        features: ['Auto metadata scraping', 'Custom thumbnails', 'Click tracking', 'Rich previews'],
        apiExample: `{
  "type": "link",
  "content": {
    "url": "https://example.com",
    "title": "Custom Title",
    "description": "Custom description"
  }
}`,
        color: 'border-green-500/20 bg-green-500/5'
    },
    {
        name: 'Kanban Board',
        icon: FaColumns,
        description: 'Interactive task management with drag-and-drop columns and cards.',
        features: ['Drag & drop', 'Custom columns', 'Card descriptions', 'Progress tracking'],
        apiExample: `{
  "type": "kanban",
  "content": {
    "columns": [
      {
        "title": "To Do",
        "cards": [{"title": "Task 1", "description": "Details"}]
      }
    ]
  }
}`,
        color: 'border-yellow-500/20 bg-yellow-500/5'
    },
    {
        name: 'Checklist Block',
        icon: FaCheckSquare,
        description: 'Interactive checklists with persistent state and progress indicators.',
        features: ['Persistent state', 'Progress tracking', 'Nested items', 'Bulk operations'],
        apiExample: `{
  "type": "checklist",
  "content": {
    "items": [
      {"text": "Item 1", "completed": false},
      {"text": "Item 2", "completed": true}
    ]
  }
}`,
        color: 'border-indigo-500/20 bg-indigo-500/5'
    },
    {
        name: 'Code Editor Block',
        icon: FaCode,
        description: 'Syntax-highlighted code blocks with multiple language support and themes.',
        features: ['50+ languages', 'Multiple themes', 'Line numbers', 'Copy to clipboard'],
        apiExample: `{
  "type": "code",
  "content": {
    "code": "console.log('Hello World');",
    "language": "javascript",
    "theme": "dark"
  }
}`,
        color: 'border-gray-500/20 bg-gray-500/5'
    },
    {
        name: 'Audio Block',
        icon: FaMusic,
        description: 'Audio player with waveform visualization and podcast-style controls.',
        features: ['Waveform display', 'Playback controls', 'Chapter markers', 'Speed control'],
        apiExample: `{
  "type": "audio",
  "content": {
    "url": "https://example.com/audio.mp3",
    "title": "Audio Title",
    "duration": 180
  }
}`,
        color: 'border-pink-500/20 bg-pink-500/5'
    },
    {
        name: 'Record/Receipt Block',
        icon: FaReceipt,
        description: 'Structured data for tracking expenses, records, and financial information.',
        features: ['Expense tracking', 'Category sorting', 'Date filtering', 'Export data'],
        apiExample: `{
  "type": "record",
  "content": {
    "type": "expense",
    "amount": 29.99,
    "date": "2024-01-15",
    "category": "Software"
  }
}`,
        color: 'border-orange-500/20 bg-orange-500/5'
    },
    {
        name: 'Social Media Share Block',
        icon: FaShareAlt,
        description: 'Social media profile links with custom icons and engagement metrics.',
        features: ['Multiple platforms', 'Custom icons', 'Analytics tracking', 'Link validation'],
        apiExample: `{
  "type": "social_media",
  "content": {
    "platform": "twitter",
    "username": "example",
    "url": "https://twitter.com/example"
  }
}`,
        color: 'border-cyan-500/20 bg-cyan-500/5'
    },
    {
        name: '3D Model Viewer (STL)',
        icon: FaCubes,
        description: 'Interactive 3D model viewer supporting STL files with zoom and rotation.',
        features: ['STL file support', '360° rotation', 'Zoom controls', 'Lighting options'],
        apiExample: `{
  "type": "stl_viewer",
  "content": {
    "url": "https://example.com/model.stl",
    "title": "3D Model",
    "description": "Model description"
  }
}`,
        color: 'border-orange-500/20 bg-orange-500/5'
    },
    {
        name: 'Shop Block',
        icon: FaShoppingCart,
        description: 'E-commerce integration with product listings and affiliate links.',
        features: ['Product catalogs', 'Affiliate tracking', 'Price monitoring', 'Amazon integration'],
        apiExample: `{
  "type": "shop",
  "content": {
    "products": [
      {
        "title": "Product Name",
        "price": "$29.99",
        "url": "https://example.com/buy"
      }
    ]
  }
}`,
        color: 'border-emerald-500/20 bg-emerald-500/5'
    },
    {
        name: 'Location Block',
        icon: FaMapMarkerAlt,
        description: 'Interactive maps with custom markers and location information.',
        features: ['Interactive maps', 'Custom markers', 'GPS coordinates', 'Location sharing'],
        apiExample: `{
  "type": "location",
  "content": {
    "latitude": 40.7128,
    "longitude": -74.0060,
    "title": "New York City",
    "description": "Location details"
  }
}`,
        color: 'border-red-500/20 bg-red-500/5'
    },
    {
        name: 'Menu Block',
        icon: FaCog,
        description: 'Structured menu system for restaurants, services, or product catalogs.',
        features: ['Category organization', 'Price display', 'Image support', 'Search functionality'],
        apiExample: `{
  "type": "menu",
  "content": {
    "categories": [
      {
        "name": "Appetizers",
        "items": [{"name": "Item", "price": "$9.99"}]
      }
    ]
  }
}`,
        color: 'border-amber-500/20 bg-amber-500/5'
    }
]

export default function BlocksPage() {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10 container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-6xl mx-auto"
                >
                    <div className="mb-8">
                        <Link href="/support/documentation" className="text-indigo-400 hover:text-indigo-300 text-sm">
                            ← Back to Documentation
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-white mb-6">
                        Block System Architecture
                    </h1>
                    
                    <div className="prose prose-lg prose-invert max-w-none">
                        <p className="text-gray-300 text-lg mb-8">
                            The heart of Autofocus is our expandable block system - a modular architecture that lets 
                            creators build rich, interactive content experiences. Each block type serves a specific 
                            purpose while maintaining consistent APIs and rendering patterns.
                        </p>

                        {/* System Overview */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🧩 How Blocks Work
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-2">Universal Structure</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Every block follows a consistent data structure with type, content, metadata, and ordering information.
                                    </p>
                                    <div className="font-mono text-xs text-gray-400 bg-neutral-800 p-3 rounded">
                                        <div>id: uuid</div>
                                        <div>type: string</div>
                                        <div>content: json</div>
                                        <div>order_index: integer</div>
                                        <div>page_id: uuid</div>
                                        <div>created_at: timestamp</div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-2">Rendering Pipeline</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Blocks are dynamically rendered based on type, with edit/view modes and real-time updates.
                                    </p>
                                    <div className="space-y-2 text-sm">
                                        <div className="text-gray-300">1. <span className="text-blue-400">Fetch</span> blocks from database</div>
                                        <div className="text-gray-300">2. <span className="text-purple-400">Sort</span> by order_index</div>
                                        <div className="text-gray-300">3. <span className="text-green-400">Render</span> appropriate component</div>
                                        <div className="text-gray-300">4. <span className="text-orange-400">Subscribe</span> to real-time updates</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Block Types Grid */}
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-white mb-6">
                                📦 Available Block Types
                            </h2>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {blockTypes.map((block, index) => (
                                    <motion.div
                                        key={block.name}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.05 }}
                                        className={`rounded-lg border p-6 backdrop-blur-sm ${block.color}`}
                                    >
                                        <div className="flex items-start gap-4 mb-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <block.icon className="text-xl" />
                                            </div>
                                            <div className="flex-grow">
                                                <h3 className="text-lg font-semibold text-white mb-2">
                                                    {block.name}
                                                </h3>
                                                <p className="text-gray-300 text-sm">
                                                    {block.description}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="mb-4">
                                            <h4 className="text-sm font-semibold text-gray-200 mb-2">Key Features</h4>
                                            <div className="flex flex-wrap gap-1">
                                                {block.features.map((feature) => (
                                                    <span key={feature} className="text-xs bg-neutral-800 text-gray-300 px-2 py-1 rounded">
                                                        {feature}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-200 mb-2">API Structure</h4>
                                            <pre className="text-xs bg-neutral-800 text-gray-300 p-3 rounded overflow-x-auto">
                                                {block.apiExample}
                                            </pre>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Block Operations */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                ⚙️ Block Operations
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">CRUD Operations</h3>
                                    <div className="space-y-3 text-sm">
                                        <div className="bg-neutral-800 p-3 rounded">
                                            <div className="font-mono text-green-400 mb-1">CREATE</div>
                                            <div className="text-gray-300">Add new blocks with automatic ordering and real-time updates</div>
                                        </div>
                                        <div className="bg-neutral-800 p-3 rounded">
                                            <div className="font-mono text-blue-400 mb-1">READ</div>
                                            <div className="text-gray-300">Fetch blocks with pagination, filtering, and caching</div>
                                        </div>
                                        <div className="bg-neutral-800 p-3 rounded">
                                            <div className="font-mono text-yellow-400 mb-1">UPDATE</div>
                                            <div className="text-gray-300">Edit content with live preview and auto-save functionality</div>
                                        </div>
                                        <div className="bg-neutral-800 p-3 rounded">
                                            <div className="font-mono text-red-400 mb-1">DELETE</div>
                                            <div className="text-gray-300">Remove blocks with confirmation and undo capabilities</div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Advanced Features</h3>
                                    <div className="space-y-3 text-sm">
                                        <div className="bg-neutral-800 p-3 rounded">
                                            <div className="font-semibold text-cyan-400 mb-1">Drag & Drop Reordering</div>
                                            <div className="text-gray-300">React DnD with touch support for mobile devices</div>
                                        </div>
                                        <div className="bg-neutral-800 p-3 rounded">
                                            <div className="font-semibold text-indigo-400 mb-1">Real-time Collaboration</div>
                                            <div className="text-gray-300">Supabase subscriptions for live updates across clients</div>
                                        </div>
                                        <div className="bg-neutral-800 p-3 rounded">
                                            <div className="font-semibold text-emerald-400 mb-1">Version History</div>
                                            <div className="text-gray-300">Track changes with automatic versioning and rollback</div>
                                        </div>
                                        <div className="bg-neutral-800 p-3 rounded">
                                            <div className="font-semibold text-pink-400 mb-1">Content Validation</div>
                                            <div className="text-gray-300">Schema validation and sanitization for security</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Developer Guide */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                👨‍💻 Creating Custom Blocks
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Block Component Structure</h3>
                                    <pre className="bg-neutral-800 p-4 rounded text-sm text-gray-300 overflow-x-auto">
{`// MyCustomBlock.js
export default function MyCustomBlock({ 
  id, 
  content, 
  isEditing, 
  onEdit, 
  onDelete,
  onReorder 
}) {
  if (isEditing) {
    return <MyCustomBlockEdit {...props} />
  }
  
  return (
    <div className="custom-block">
      {/* Render block content */}
    </div>
  )
}`}
                                    </pre>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Registration Process</h3>
                                    <div className="space-y-2 text-sm text-gray-300">
                                        <div>1. Create block component with view and edit modes</div>
                                        <div>2. Add to block registry in <code className="bg-neutral-800 px-2 py-1 rounded">_blocks/index.js</code></div>
                                        <div>3. Define content schema and validation rules</div>
                                        <div>4. Add database migration for new block type</div>
                                        <div>5. Update block selector UI components</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Performance Considerations */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                ⚡ Performance & Optimization
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-yellow-400 mb-3">Rendering Optimization</h3>
                                    <ul className="text-gray-300 text-sm space-y-2">
                                        <li>• Virtual scrolling for large block lists</li>
                                        <li>• Lazy loading of heavy block types</li>
                                        <li>• Memoization of block components</li>
                                        <li>• Debounced auto-save functionality</li>
                                        <li>• Progressive image loading</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">Data Optimization</h3>
                                    <ul className="text-gray-300 text-sm space-y-2">
                                        <li>• Selective field fetching from database</li>
                                        <li>• Client-side caching with state management</li>
                                        <li>• Optimistic updates for instant feedback</li>
                                        <li>• Batch operations for bulk changes</li>
                                        <li>• Content compression for large blocks</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">
                                🚀 Explore More
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Link href="/support/documentation/api" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-green-400 mb-1">API Documentation →</div>
                                    <div className="text-gray-300 text-sm">Learn how to interact with blocks via REST API</div>
                                </Link>
                                <Link href="/support/documentation/database" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-blue-400 mb-1">Database Schema →</div>
                                    <div className="text-gray-300 text-sm">Understand the underlying data structure</div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}